import { Elements } from '@elements/index.ts';
import { Link, useNavigate } from 'react-router';
import { ShopPlansTabs } from '@elements/ShopPlansTabs';
import ArrowPointer from '@/assets/icons/ArrowPointer.svg';
import TryItNow from '@/assets/icons/TryItNow.svg';
import TryItNowDown from '@/assets/icons/TryItNowDown.svg';
import featuresBackground from '@images/hero-bg.webp';
import {
    buildShopPlansCountyPath,
    buildShopPlansStatePath,
    normalizeCountySlug,
    SHOP_PLANS_BASE_PATH,
} from '@/lib/shopPlansRouting';
import { getCountySlugByCode } from '@/utils/counties';
import Image from 'next/image';

type ShopPlansData = {
    zipCode?: string;
    age?: string;
    ages?: string[];
    familyMembersCount?: number;
    state?: string;
    county?: string;
};

export const HeroSection = () => {
    const navigate = useNavigate();

    const handleShopPlans = async (data: ShopPlansData) => {
        if (data.zipCode) {
            const params = new URLSearchParams();
            params.set('tab', 'age-zip');
            params.set('zipCode', data.zipCode);

            const validAges = data.ages && data.ages.length > 0
                ? data.ages.filter((age) => age.trim() !== '')
                : (data.age ? [data.age] : []);

            if (validAges.length > 0) {
                validAges.forEach((age) => {
                    params.append('age', age);
                });
            }
            navigate(`${SHOP_PLANS_BASE_PATH}?${params.toString()}`);
        } else if (data.state) {
            const stateCode = data.state.toUpperCase();
            let countySlug = '';
            if (data.county) {
                countySlug =
                    (await getCountySlugByCode(stateCode, data.county)) ??
                    normalizeCountySlug(data.county);
            }

            if (countySlug) {
                navigate(buildShopPlansCountyPath(stateCode, countySlug));
                return;
            }

            navigate(buildShopPlansStatePath(stateCode));
        } else {
            navigate(SHOP_PLANS_BASE_PATH);
        }
    };
    return (
        <section className="relative overflow-visible pt-5  md:pt-20 md:pb-30 lg:pt-20 lg:pb-20">
            <div className="relative z-10 flex flex-col items-center justify-center lg:flex-row lg:items-center lg:justify-between">
                <div className="h-auto pb-10 px-5 w-full lg:w-6/12 lg:max-w-[1400px] lg:pl-[79px] lg:h-[480px]">
                    <div className="flex h-full w-full flex-col items-center justify-center gap-4 overflow-hidden lg:items-start lg:justify-center">
                        <div className="flex shrink-0 flex-col items-center justify-center gap-3 lg:items-start">
                            <Elements.Title
                                title="White-Labeled ICHRA for Brokers & TPAs"
                                type="h1"
                                className="text-center font-bold text-brand-700 md:max-w-[540px] lg:max-w-none lg:text-start"
                            />
                            <Elements.Description
                                description="Predictable Benefits is a technology solution built from the ground-up for full control and customization.  Control 100% of commissions, set your own fees, and deliver ICHRA solutions under your own brand with our enterprise-grade platform."
                                type="small"
                                className="max-w-[520px] text-sm md:text-base text-center font-regular text-gray-900 lg:text-start"
                            />
                        </div>

                        <div className="flex w-full shrink-0 flex-row items-center justify-center gap-4 sm:flex-row sm:w-auto lg:justify-start">
                            <Link to="/demo" className="inline-flex w-full sm:w-auto">
                                <Elements.Button
                                    variant="primary"
                                    size="md"
                                    className="w-full h-11 justify-center whitespace-nowrap sm:w-auto lg:h-14 lg:px-6"
                                >
                                    <span className="text-base md:text-lg lg:text-[20px]">Book a Demo</span>
                                </Elements.Button>
                            </Link>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col items-center pb-2 justify-center md:hidden lg:hidden">
                    <Image src={TryItNowDown} alt="Try It Now Down" width={200} height={40} />
                </div>
                <div
                    className="flex h-auto w-full py-10 px-5 rounded-t-xl lg:rounded-t-none lg:rounded-l-3xl lg:w-6/12 lg:h-[480px] lg:pr-0 items-center justify-center lg:justify-start bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: `url(${featuresBackground})`,
                    }}
                >
                    <div className="flex h-full w-full max-w-[500px] flex-col items-center justify-center gap-4 lg:justify-between lg:ml-30">
                        <div className="flex flex-col items-center justify-center">
                            <Elements.Title
                                title="Shop Individual Health Plans"
                                type="h3"
                                className="text-center font-bold text-gray-50"
                            />
                            <Elements.Description
                                description="Find the available health coverage."
                                type="large"
                                className="text-center font-normal text-gray-50"
                            />
                        </div>
                        <div className="flex w-full flex-col items-center gap-2">
                            <ShopPlansTabs onShopPlans={handleShopPlans} />
                            <div className="hidden md:flex items-end justify-center gap-2">
                                <Image src={ArrowPointer} alt="Arrow Pointer" width={103} height={76} />
                                <Image src={TryItNow} alt="Try It Now" width={215} height={40} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
